import type {UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {extractImageFilesFromClipboard, PLAIN_TEXT_CLIPBOARD_MIMETYPE} from '@Utils/clipboard.util';
import type {ClipboardEvent} from 'react';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {ImageSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {ImageBackgroundObject} from '@PosterWhiteboard/page/background/image-background.class';

type AttachImagesCallback = (attachments: UserImageAttachment[]) => void;

interface PosterItemsPasteOptions {
  pasteAsReferenceImages: boolean;
  onReferenceImageFiles: (files: File[]) => void;
  onAttachImages: AttachImagesCallback;
}

export const handlePosterItemsPaste = (event: ClipboardEvent, options: PosterItemsPasteOptions): boolean => {
  const clipboardText = event.clipboardData?.getData(PLAIN_TEXT_CLIPBOARD_MIMETYPE);
  if (!clipboardText) {
    return false;
  }
  const imageItems = getClipboardPosterImageItems(clipboardText);
  if (imageItems === null || imageItems.length === 0) {
    return false;
  }

  event.preventDefault();
  if (options.pasteAsReferenceImages) {
    const pastedImageFiles = extractImageFilesFromClipboard(event.clipboardData?.items);
    if (pastedImageFiles.length > 0) {
      options.onReferenceImageFiles([pastedImageFiles[0]]);
      return true;
    }
  }
  options.onAttachImages([toImageAttachment(imageItems[0])]);
  return true;
};

const getClipboardPosterImageItems = (text: string): ImageItemObject[] | null => {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{')) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  const graphicItemObjects = extractGraphicItemObjects(parsed);
  if (graphicItemObjects === null) {
    return null;
  }
  const imageItems = graphicItemObjects.flatMap(extractImageItemObjects);
  const backgroundImageItem = extractBackgroundImageItemObject(parsed);
  if (backgroundImageItem) {
    imageItems.push(backgroundImageItem);
  }
  return imageItems.filter(isUploadedImageItem);
};

const extractBackgroundImageItemObject = (parsed: unknown): ImageItemObject | null => {
  if (!parsed || typeof parsed !== 'object' || !('backgroundItemObject' in parsed)) {
    return null;
  }
  const background = parsed.backgroundItemObject;
  if (!background || typeof background !== 'object') {
    return null;
  }
  const item = background as ItemObject;
  return isImageBackgroundItemObject(item) ? item : null;
};

const extractImageItemObjects = (item: ItemObject): ImageItemObject[] => {
  if (isImageItemObject(item)) {
    return [item];
  }
  if (isSlideshowItemObject(item)) {
    const imageSlides = item.slides.slidesOrder.map((slideId) => item.slides.slidesHashMap[slideId]).filter(isImageSlideItemObject);
    const selectedSlide = imageSlides.find((slide) => slide.uid === item.selectedSlideUID);
    return selectedSlide ? [selectedSlide, ...imageSlides.filter((slide) => slide !== selectedSlide)] : imageSlides;
  }
  return [];
};

const extractGraphicItemObjects = (parsed: unknown): ItemObject[] | null => {
  if (!parsed || typeof parsed !== 'object' || !('graphicItemObjects' in parsed)) {
    return null;
  }
  const items = parsed.graphicItemObjects;
  if (!Array.isArray(items)) {
    return null;
  }
  return items.filter((item): item is ItemObject => typeof item === 'object' && item !== null);
};

export const isImageItemObject = (item: ItemObject): item is ImageItemObject => {
  return item.gitype === ITEM_TYPE.IMAGE;
};

export const isSlideshowItemObject = (item: ItemObject): item is SlideshowItemObject => {
  return item.gitype === ITEM_TYPE.SLIDESHOW;
};

export const isImageSlideItemObject = (item: ItemObject): item is ImageSlideItemObject => {
  return item.gitype === ITEM_TYPE.IMAGESLIDE;
};

export const isImageBackgroundItemObject = (item: ItemObject): item is ImageBackgroundObject => {
  return item.gitype === ITEM_TYPE.IMAGEBACKGROUND;
};

export const isUploadedImageItem = (item: ImageItemObject): boolean => {
  return Boolean(item.hashedFilename) && Boolean(item.fileExtension);
};

const toImageAttachment = (item: ImageItemObject): UserImageAttachment => {
  return {
    type: PromptAttachmentType.IMAGE,
    hashedFilename: item.hashedFilename ?? '',
    extension: item.fileExtension ?? '',
    source: item.imageSource,
    width: item.loadedImageWidth,
    height: item.loadedImageHeight,
  };
};
